import { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { List, Search, X } from 'lucide-react';
import { searchMembers, getMember } from '../api';
import styles from './MemberSearchInput.module.css';

// ---------------------------------------------------------------------------
// useDebounce
// ---------------------------------------------------------------------------

export function useDebounce(value, delay) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

// ---------------------------------------------------------------------------
// MemberPickerModal — paginated browse of the full member list
// ---------------------------------------------------------------------------

export function MemberPickerModal({ onSelect, onClose }) {
  const [items, setItems] = useState([]);
  const [nextCursor, setNextCursor] = useState(null);
  const [cursorHistory, setCursorHistory] = useState([]);
  const [total, setTotal] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchPage = useCallback((cursor) => {
    let cancelled = false;
    setLoading(true);
    const params = { limit: 20 };
    if (cursor) params.cursor = cursor;
    searchMembers(params)
      .then((data) => {
        if (cancelled) return;
        setItems(data.items || []);
        setNextCursor(data.next_cursor || null);
        setTotal(data.total ?? null);
      })
      .catch(() => {
        if (!cancelled) setItems([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  // Load first page on mount
  useEffect(() => {
    return fetchPage(null);
  }, [fetchPage]);

  const handleNext = useCallback(() => {
    if (!nextCursor) return;
    setCursorHistory((prev) => [...prev, nextCursor]);
    fetchPage(nextCursor);
  }, [nextCursor, fetchPage]);

  const handlePrev = useCallback(() => {
    if (cursorHistory.length === 0) return;
    const history = [...cursorHistory];
    history.pop();
    setCursorHistory(history);
    const targetCursor = history.length > 0 ? history[history.length - 1] : null;
    fetchPage(targetCursor);
  }, [cursorHistory, fetchPage]);

  const handleRowSelect = useCallback((member) => {
    onSelect(member);
    onClose();
  }, [onSelect, onClose]);

  const handleOverlayClick = useCallback((e) => {
    if (e.target === e.currentTarget) onClose();
  }, [onClose]);

  const isPrevDisabled = cursorHistory.length === 0;
  const isNextDisabled = !nextCursor;

  const totalLabel = total !== null
    ? `${total} member${total !== 1 ? 's' : ''}`
    : null;

  return createPortal(
    <div
      className={styles.pickerOverlay}
      onClick={handleOverlayClick}
    >
      <div
        className={styles.pickerShell}
        role="dialog"
        aria-modal="true"
        aria-label="Browse members"
      >
        <div className={styles.pickerHeader}>
          <span className={styles.pickerTitle}>Browse members</span>
          <button
            type="button"
            className={styles.pickerCloseBtn}
            onClick={onClose}
            aria-label="Close"
          >
            <X size={16} strokeWidth={1.5} />
          </button>
        </div>
        <div className={styles.pickerBody}>
          {loading ? (
            <div className={styles.pickerEmpty}>Loading…</div>
          ) : items.length === 0 ? (
            <div className={styles.pickerEmpty}>No members found.</div>
          ) : (
            <div className={styles.pickerTableWrap}>
              <table className={styles.pickerTable}>
                <thead>
                  <tr>
                    <th className={styles.pickerTh}>Name</th>
                    <th className={styles.pickerTh}>Member #</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((m) => (
                    <tr
                      key={m.id}
                      className={styles.pickerTr}
                      onClick={() => handleRowSelect(m)}
                    >
                      <td className={styles.pickerTd}>
                        <span className={styles.pickerMemberName}>{m.member_name}</span>
                      </td>
                      <td className={styles.pickerTd}>
                        <span className={styles.pickerMemberNumber}>{m.member_number}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {!loading && (
            <div className={styles.pickerPagination}>
              <span className={styles.pickerTotal}>{totalLabel}</span>
              <div style={{ display: 'flex', gap: 6 }}>
                <button
                  type="button"
                  className={styles.pickerPageBtn}
                  onClick={handlePrev}
                  disabled={isPrevDisabled}
                  aria-label="Previous page"
                >
                  &larr; Previous
                </button>
                <button
                  type="button"
                  className={styles.pickerPageBtn}
                  onClick={handleNext}
                  disabled={isNextDisabled}
                  aria-label="Next page"
                >
                  Next &rarr;
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}

// ---------------------------------------------------------------------------
// MemberSearchInput — debounced autocomplete + CMS lookup + browse modal
// ---------------------------------------------------------------------------

export default function MemberSearchInput({ value, onSelect, placeholder, disabled }) {
  const [query, setQuery] = useState(value || '');
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [cmsLoading, setCmsLoading] = useState(false);
  const [cmsError, setCmsError] = useState('');
  const wrapRef = useRef(null);
  const skipNextSearch = useRef(false);

  // Sync external value (e.g. when row is reset or edit mode starts).
  // Set the skip guard BEFORE setQuery so the debounced effect that fires
  // next tick sees it and does not trigger a search.
  useEffect(() => {
    skipNextSearch.current = true;
    setQuery(value || '');
  }, [value]);

  // Debounced query for autocomplete — cache-only, never hits CMS
  const debouncedQuery = useDebounce(query, 300);

  useEffect(() => {
    if (skipNextSearch.current) {
      skipNextSearch.current = false;
      return;
    }
    if (!debouncedQuery || debouncedQuery.length < 1) {
      setResults([]);
      setOpen(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    searchMembers({ name: debouncedQuery, memberNumber: debouncedQuery, limit: 8, cacheOnly: true })
      .then((data) => {
        if (!cancelled) {
          setResults(data.items || []);
          setOpen((data.items || []).length > 0);
        }
      })
      .catch(() => {
        if (!cancelled) setResults([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [debouncedQuery]);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleSelect = (member) => {
    skipNextSearch.current = true;
    setQuery(member.member_name);
    setOpen(false);
    onSelect(member);
  };

  const handlePickerSelect = useCallback((member) => {
    skipNextSearch.current = true;
    setQuery(member.member_name);
    setOpen(false);
    onSelect(member);
  }, [onSelect]);

  const handleBrowseClick = useCallback((e) => {
    e.preventDefault();
    setOpen(false);
    setPickerOpen(true);
  }, []);

  const handleCmsLookup = useCallback(async (e) => {
    e.preventDefault();
    if (!query) return;
    setCmsError('');
    setCmsLoading(true);
    setOpen(false);
    try {
      const data = await getMember(query);
      if (data?.member) {
        handleSelect(data.member);
      }
    } catch (err) {
      setCmsError(err.message || 'Member not found.');
    } finally {
      setCmsLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  return (
    <div className={styles.outerWrap}>
      <div ref={wrapRef} className={styles.wrap}>
        <input
          type="text"
          className={styles.input}
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setCmsError('');
            if (!e.target.value) onSelect(null);
          }}
          placeholder={placeholder || 'Search member…'}
          autoComplete="off"
          aria-label="Search member"
          disabled={disabled}
        />
        <button
          type="button"
          className={styles.iconBtn}
          onClick={handleBrowseClick}
          title="Browse members"
          aria-label="Browse members"
          disabled={disabled}
        >
          <List size={14} strokeWidth={1.5} />
        </button>
        <button
          type="button"
          className={styles.iconBtn}
          title="Look up in CMS by member number"
          aria-label="Look up in CMS by member number"
          disabled={!query || cmsLoading || disabled}
          onClick={handleCmsLookup}
        >
          <Search size={14} strokeWidth={1.5} />
        </button>
        {open && (
          <div className={styles.dropdown} role="listbox">
            {loading && (
              <div className={styles.dropdownEmpty}>Searching…</div>
            )}
            {!loading && results.length === 0 && (
              <div className={styles.dropdownEmpty}>No members found.</div>
            )}
            {!loading && results.map((m) => (
              <div
                key={m.id}
                className={styles.dropdownItem}
                role="option"
                aria-selected={false}
                onMouseDown={(e) => {
                  e.preventDefault();
                  handleSelect(m);
                }}
              >
                <span>{m.member_name}</span>
                <span className={styles.dropdownItemNumber}>{m.member_number}</span>
              </div>
            ))}
          </div>
        )}
      </div>
      {cmsError && (
        <p className={styles.cmsError} role="alert">{cmsError}</p>
      )}
      {pickerOpen && (
        <MemberPickerModal
          onSelect={handlePickerSelect}
          onClose={() => setPickerOpen(false)}
        />
      )}
    </div>
  );
}
